// src/components/BoardPack/components/consolidate_boardpack_components/Bridge_Summary_Chart_component.jsx
//
// UPDATE:
//  1) Value label sign se position hota hai: positive => bar ke UPAR,
//     negative => bar ke NEECHE.
//  2) Bars ke darmiyan connector lines (halki patli lines) add ki gayi hain.
//  3) Overlap fix:
//     - Value labels pe white halo (connector line text ke beech se na guzre)
//     - X-axis labels chartArea.bottom ke neeche shift (negative labels ke liye gutter)
//     - Manual yMin/yMax ki jagah Chart.js "grace" (15%) use hota hai
//     - Bottom padding aur panel height barhayi gayi
import React, { useEffect, useRef, useState } from "react";
import { Box, Typography } from "@mui/material";
import {
  Chart,
  BarController,
  BarElement,
  CategoryScale,
  LinearScale,
  Tooltip,
  Legend,
} from "chart.js";
import { THEME } from "../../constant/color";
import HeadingComponent from "../shared/heading";

Chart.register(BarController, BarElement, CategoryScale, LinearScale, Tooltip, Legend);
Chart.defaults.color = THEME.colors.black; // global fallback so axis text is readable on white bg

// Har number ko "153,618" jaisa format karta hai; negative ko (497) style
const fmt = (n) => {
  if (n === null || n === undefined || isNaN(n)) return "-";
  const abs = Math.abs(Math.round(n));
  const str = abs.toLocaleString("en-US");
  return n < 0 ? `(${str})` : str;
};

// Waterfall math: pehla/aakhri item "total" (full bar, base 0), darmiyan
// wale items floating bars — running total se offset. type na diya ho to
// khud decide: pehla/aakhri = total, baaki: value>=0 => increase, else decrease
const buildWaterfall = (series) => {
  if (!series?.length) return [];

  let running = 0;
  return series.map((item, idx) => {
    const isEdge = idx === 0 || idx === series.length - 1;
    const type = item.type || (isEdge ? "total" : item.value >= 0 ? "increase" : "decrease");

    if (type === "total") {
      running = item.value;
      return { label: item.label, from: 0, to: item.value, display: item.value, type };
    }

    const start = running;
    const end = running + item.value;
    running = end;

    return {
      label: item.label,
      from: Math.min(start, end),
      to: Math.max(start, end),
      display: item.value,
      type,
    };
  });
};

const barColor = (type) => {
  if (type === "increase") return THEME.colors.increase;
  if (type === "decrease") return THEME.colors.decrease;
  return THEME.colors.total;
};

// ✅ Value label plugin — SIGN ke hisaab se position:
//    positive value => bar ke UPAR
//    negative value => bar ke NEECHE
//    + white halo taake connector line text ke beech se na guzre
const valueLabelPlugin = {
  id: "valueLabelPlugin",
  afterDatasetsDraw(chart) {
    const { ctx, scales } = chart;
    const meta = chart.getDatasetMeta(0);
    const yScale = scales.y;

    ctx.save();
    ctx.font = "11px sans-serif";
    ctx.textAlign = "center";

    meta.data.forEach((bar, i) => {
      const raw = chart.data.datasets[0].rawData[i];
      const pixelFrom = yScale.getPixelForValue(raw.from);
      const pixelTo = yScale.getPixelForValue(raw.to);
      const visualTop = Math.min(pixelFrom, pixelTo);
      const visualBottom = Math.max(pixelFrom, pixelTo);

      const text = fmt(raw.display);
      const isPos = raw.display >= 0;
      const y = isPos ? visualTop - 3 : visualBottom + 3;
      ctx.textBaseline = isPos ? "bottom" : "top";

      // white halo
      ctx.lineWidth = 3;
      ctx.lineJoin = "round";
      ctx.strokeStyle = "#fff";
      ctx.strokeText(text, bar.x, y);

      ctx.fillStyle = THEME.colors.black;
      ctx.fillText(text, bar.x, y);
    });

    ctx.restore();
  },
};

// ✅ Connector lines — har bar ke end level se agli bar ke start level tak
// halki patli line
const connectorPlugin = {
  id: "connectorPlugin",
  beforeDatasetsDraw(chart) {
    const { ctx, scales } = chart;
    const meta = chart.getDatasetMeta(0);
    const raws = chart.data.datasets[0].rawData;
    const yScale = scales.y;

    ctx.save();
    ctx.strokeStyle = "rgba(0, 0, 0, 0.2)";
    ctx.lineWidth = 1;

    for (let i = 0; i < meta.data.length - 1; i++) {
      const raw = raws[i];
      const t = String(raw.type || "").trim().toLowerCase();
      // is bar ke khatam hone ka level (running total)
      const endValue =
        t === "increase" ? raw.to : t === "decrease" ? raw.from : raw.display;

      const y = Math.round(yScale.getPixelForValue(endValue)) + 0.5;
      const cur = meta.data[i];
      const next = meta.data[i + 1];

      ctx.beginPath();
      ctx.moveTo(cur.x + cur.width / 2, y);
      ctx.lineTo(next.x - next.width / 2, y);
      ctx.stroke();
    }
    ctx.restore();
  },
};

// Custom legend renderer — draws Increase/Decrease/Total swatches + black
// text directly on each panel's own canvas, at the top of that panel.
const customLegendPlugin = {
  id: "customLegendPlugin",
  afterDraw(chart) {
    const { ctx, chartArea } = chart;
    const items = [
      { text: "Increase", color: THEME.colors.increase },
      { text: "Decrease", color: THEME.colors.decrease },
      { text: "Total", color: THEME.colors.total },
    ];

    ctx.save();
    ctx.font = "600 11px sans-serif";
    ctx.textBaseline = "middle";

    const boxSize = 8;
    const gapAfterBox = 5;
    const gapBetweenItems = 14;
    const widths = items.map((it) => boxSize + gapAfterBox + ctx.measureText(it.text).width);
    const totalWidth = widths.reduce((a, b) => a + b, 0) + gapBetweenItems * (items.length - 1);

    let x = chartArea.left + (chartArea.right - chartArea.left - totalWidth) / 2;
    const y = 9; // legend canvas ke bilkul upar

    items.forEach((item, i) => {
      ctx.fillStyle = item.color;
      ctx.fillRect(x, y - boxSize / 2, boxSize, boxSize);

      ctx.fillStyle = "rgba(0, 0, 0, 0.65)";
      ctx.fillText(item.text, x + boxSize + gapAfterBox, y);

      x += widths[i] + gapBetweenItems;
    });

    ctx.restore();
  },
};

// X-axis labels ko stagger karke draw karta hai — even index wale labels
// upar wali row mein, odd index wale labels neeche wali row mein, taake
// lambe labels overlap na hon (default rotated ticks ki jagah).
// ✅ Ab labels chartArea.bottom ke 22px neeche shuru hote hain, taake
// negative value labels (bar ke neeche) ke liye gutter bache.
const staggeredLabelPlugin = {
  id: "staggeredLabelPlugin",
  afterDraw(chart) {
    const { ctx, chartArea } = chart;
    const meta = chart.getDatasetMeta(0);

    ctx.save();
    ctx.font = "600 9px sans-serif";
    ctx.fillStyle = "rgba(0, 0, 0, 0.65)";
    ctx.textAlign = "center";
    ctx.textBaseline = "top";

    const baseY = chartArea.bottom + 22;
    const rowGap = 11;

    meta.data.forEach((bar, i) => {
      const label = chart.data.labels[i];
      const y = baseY + (i % 2 === 0 ? 0 : rowGap);
      ctx.fillText(label, bar.x, y);
    });

    ctx.restore();
  },
};

// ── Single waterfall panel (Chart.js) ─────────────────────────────────
function WaterfallPanel({
  panelTitle,
  data,
  height = 180, // extra padding ki wajah se barhaya
  titleFontSize = 8.5,
  footerLabel = "Profit and Loss", // chart ke neeche center mein dikhne wala label
}) {
  const canvasRef = useRef(null);
  const chartRef = useRef(null);
  const containerRef = useRef(null);

  useEffect(() => {
    if (!canvasRef.current) return;

    const waterfall = buildWaterfall(data);

    if (chartRef.current) {
      chartRef.current.destroy();
    }

    chartRef.current = new Chart(canvasRef.current, {
      type: "bar",
      data: {
        labels: waterfall.map((d) => d.label),
        datasets: [
          {
            data: waterfall.map((d) => [d.from, d.to]),
            rawData: waterfall,
            backgroundColor: waterfall.map((d) => barColor(d.type)),
            borderWidth: 0,
            barPercentage: 0.55,
          },
        ],
      },
      options: {
        responsive: false, // manual control — auto-resize race hi blur ki asal wajah thi
        maintainAspectRatio: false,
        clip: false,
        layout: { padding: { top: 46, bottom: 56 } }, // top barhaya: legend aur '0' labels alag rahein
        devicePixelRatio: Math.max(window.devicePixelRatio || 1, 4),

        plugins: {
          legend: { display: false }, // built-in legend off — customLegendPlugin draw karta hai
          tooltip: {
            callbacks: {
              label: (ctx) => fmt(ctx.dataset.rawData[ctx.dataIndex].display),
            },
          },
        },
        scales: {
          x: {
            ticks: { display: false }, // default ticks off — staggeredLabelPlugin draw karta hai
            grid: { display: false },
          },
          y: {
            grace: "15%", // dono taraf headroom (manual yMin/yMax ki jagah)
            ticks: {
              color: THEME.colors.black,
              font: { size: 11 },
              callback: (v) => fmt(v),
            },
            grid: { display: false },
          },
        },
      },
      // connectorPlugin sabse pehle, taake bars ke peeche draw ho
      plugins: [connectorPlugin, valueLabelPlugin, customLegendPlugin, staggeredLabelPlugin],
    });

    // Manual resize — container ka exact offsetWidth/offsetHeight measure
    // kar ke chart.resize(w, h) explicitly call karte hain.
    requestAnimationFrame(() => {
      if (containerRef.current && chartRef.current) {
        chartRef.current.resize(
          Math.round(containerRef.current.offsetWidth),
          Math.round(containerRef.current.offsetHeight)
        );
      }
    });

    const resizeObserver = new ResizeObserver(() => {
      if (!containerRef.current || !chartRef.current) return;
      const w = Math.round(containerRef.current.offsetWidth);
      const h = Math.round(containerRef.current.offsetHeight);
      chartRef.current.resize(w, h);
    });
    if (containerRef.current) resizeObserver.observe(containerRef.current);

    return () => {
      resizeObserver.disconnect();
      if (chartRef.current) {
        chartRef.current.destroy();
        chartRef.current = null;
      }
    };
  }, [data]);

  return (
    <Box
      sx={{
        flex: 1,
        minWidth: 0,
        bgcolor: THEME.colors.white,
        borderRadius: "4px",
        border: `1px solid ${THEME.colors.black}`,
        p: 1,
      }}
      className="!border"
    >
      <Typography
        align="center"
        sx={{
          color: THEME.colors.black,
          fontSize: titleFontSize,
          fontWeight: 700,
          letterSpacing: 0.3,
          mb: 0.5,
          textTransform: "uppercase",
        }}
      >
        {panelTitle}
      </Typography>

      <Box ref={containerRef} sx={{ height, position: "relative" }}>
        <canvas ref={canvasRef} />
      </Box>

      {/* Center footer label (e.g. "Profit and Loss") */}
      {footerLabel && (
        <Typography
          align="center"
          sx={{
            color: "rgba(0, 0, 0, 0.65)",
            fontSize: 10,
            fontWeight: 500,
            mt: 0.5,
          }}
        >
          {footerLabel}
        </Typography>
      )}
    </Box>
  );
}

// ── One row of N comparison panels (e.g. vs Prior Year / vs Budget / vs Forecast) ──
function PanelRow({ panels, footerLabel }) {
  return (
    <Box sx={{ display: "flex", gap: 1.5 }} className="!border p-1">
      {panels.map((p, i) => (
        <WaterfallPanel
          key={p.key ?? i}
          panelTitle={p.panelTitle}
          data={p.data}
          footerLabel={footerLabel}
        />
      ))}
    </Box>
  );
}

// ── Main presentational component (NO API calls here — page does that) ──
// One page title, dynamic N-comparison grid (month row + YTD row), single
// shared comments box, logo bottom-right. Panel count is NOT hardcoded to
// 3 — however many comparison entries the page passes in, that many
// columns render.
const Bridge_Summary_Chart_component = ({
  title,
  subtitle,
  showComments = true,
  monthPanels = [],
  ytdPanels = [],
  footerLabel = "Profit and Loss",
}) => {
  const [comments, setComments] = useState("");

  return (
    <Box
      sx={{
        width: "100%",
        height: "100%",
        bgcolor: THEME.colors.white,
        paddingX: 2,
        display: "flex",
        flexDirection: "column",
        boxSizing: "border-box",
        gap: 1.5,
      }}
    >
      {/* ── Title ── */}
      <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center" }}>
        {title && (
          <HeadingComponent variant="main">{title}</HeadingComponent>
        )}
        {subtitle && (
          <HeadingComponent variant="main">{subtitle}</HeadingComponent>
        )}
      </Box>

      <div className="">
        {/* ── Month row ── */}
        {monthPanels.length > 0 && <PanelRow panels={monthPanels} footerLabel={footerLabel} />}
        {/* ── YTD row ── */}
        {ytdPanels.length > 0 && <PanelRow panels={ytdPanels} footerLabel={footerLabel} />}
      </div>

      {/* ── Comments ── */}
      {showComments && (
        <div className="!border-2">
          <div className="border border-black p-2 bg-white">
            <div className="text-left font-semibold text-sm mb-1 border-b border-transparent">
              Comments
            </div>
            <textarea
              className="w-full h-12 p-2 resize-none outline-none text-sm focus:ring-0 border-0 bg-transparent"
              value={comments}
              onChange={(e) => setComments(e.target.value)}
            />
          </div>
        </div>
      )}
    </Box>
  );
};

export default Bridge_Summary_Chart_component;